import React, { useState, useEffect } from 'react';
import AdminHeader from '../../components/admin/AdminHeader';
import { reportService } from '../../services/reportService';
import { DailySummaryResponse, BarberPerformanceResponse } from '../../types/report.types';
import KpiCard from '../../components/admin/KpiCard';
import BarberPerformanceTable from '../../components/admin/BarberPerformanceTable';
import Spinner from '../../components/common/Spinner';
import { getLocalDateString } from '../../utils/dateUtils';

type ViewMode = 'diario' | 'mensual';

const Reports: React.FC = () => {
    const today = getLocalDateString();
    const currentMonth = today.substring(0, 7); // YYYY-MM

    const [viewMode, setViewMode] = useState<ViewMode>('diario');
    const [date, setDate] = useState<string>(today);
    const [month, setMonth] = useState<string>(currentMonth);
    
    const [summary, setSummary] = useState<DailySummaryResponse | null>(null);
    const [performances, setPerformances] = useState<BarberPerformanceResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getDates = () => {
        if (viewMode === 'diario') {
            return { start: date, end: date };
        } else {
            // Mensual
            const [y, m] = month.split('-');
            const startDate = `${month}-01`;
            const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
            const endDate = `${month}-${lastDay}`;
            return { start: startDate, end: endDate };
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const { start, end } = getDates();
            const summaryData = await reportService.getSummary(start, end);
            const performanceData = await reportService.getBarberPerformance(start, end);
            
            setSummary(summaryData);
            setPerformances(performanceData);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError('Error al cargar los reportes. Intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [viewMode, date, month]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount);
    };

    return (
        <>
            <AdminHeader title="📊 Reportes y Estadísticas" />
            <div className="container fade-in py-6" style={{ marginTop: 'var(--spacing-md)' }}>
                
                {/* Controles Principales */}
                <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', padding: '1rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                        
                        {/* Toggle Diario / Mensual */}
                        <div style={{ display: 'flex', backgroundColor: 'var(--background)', padding: '0.25rem', borderRadius: '0.5rem' }}>
                            <button 
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    borderRadius: '0.375rem', 
                                    fontSize: '0.875rem', 
                                    fontWeight: '500', 
                                    border: 'none', 
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-base)',
                                    backgroundColor: viewMode === 'diario' ? 'var(--surface)' : 'transparent',
                                    color: viewMode === 'diario' ? 'var(--accent)' : 'var(--text-secondary)',
                                    boxShadow: viewMode === 'diario' ? 'var(--shadow-sm)' : 'none'
                                }}
                                onClick={() => setViewMode('diario')}
                            >
                                Reporte por Día
                            </button>
                            <button 
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    borderRadius: '0.375rem', 
                                    fontSize: '0.875rem', 
                                    fontWeight: '500', 
                                    border: 'none', 
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-base)',
                                    backgroundColor: viewMode === 'mensual' ? 'var(--surface)' : 'transparent',
                                    color: viewMode === 'mensual' ? 'var(--accent)' : 'var(--text-secondary)',
                                    boxShadow: viewMode === 'mensual' ? 'var(--shadow-sm)' : 'none'
                                }}
                                onClick={() => setViewMode('mensual')}
                            >
                                Reporte Mensual
                            </button>
                        </div>

                        {/* Selector de Fecha o Mes */}
                        {viewMode === 'diario' ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--surface)', padding: '0.25rem', borderRadius: '0.5rem', border: '1px solid var(--secondary)' }}>
                                <button 
                                    className="btn btn-sm" 
                                    style={{ backgroundColor: 'transparent', color: 'var(--text-secondary)', padding: '0.25rem 0.5rem', border: 'none' }}
                                    onClick={() => {
                                        const [y, m, d] = date.split('-').map(Number);
                                        const dateObj = new Date(y, m - 1, d);
                                        dateObj.setDate(dateObj.getDate() - 1);
                                        setDate(getLocalDateString(dateObj));
                                    }}
                                >
                                    ◀
                                </button>
                                <input 
                                    type="date" 
                                    id="datePicker"
                                    style={{ 
                                        backgroundColor: 'transparent', 
                                        color: 'var(--text-primary)', 
                                        border: 'none',
                                        outline: 'none',
                                        fontWeight: '500',
                                        padding: '0.25rem',
                                        colorScheme: 'dark'
                                    }}
                                    value={date}
                                    max={today}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                                <button 
                                    className="btn btn-sm" 
                                    style={{ backgroundColor: 'transparent', color: date >= today ? 'var(--secondary)' : 'var(--text-secondary)', padding: '0.25rem 0.5rem', border: 'none', cursor: date >= today ? 'default' : 'pointer' }}
                                    onClick={() => {
                                        if (date >= today) return;
                                        const [y, m, d] = date.split('-').map(Number);
                                        const dateObj = new Date(y, m - 1, d);
                                        dateObj.setDate(dateObj.getDate() + 1);
                                        setDate(getLocalDateString(dateObj));
                                    }}
                                    disabled={date >= today}
                                >
                                    ▶
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem', maxWidth: '100%', scrollbarWidth: 'none' }}>
                                {[0, 1, 2, 3, 4, 5].map(offset => {
                                    const d = new Date(today);
                                    d.setMonth(d.getMonth() - offset);
                                    const mStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                    const label = new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(d);
                                    const isSelected = month === mStr;
                                    
                                    return (
                                        <button 
                                            key={mStr}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                borderRadius: '2rem',
                                                border: '1px solid',
                                                borderColor: isSelected ? 'var(--accent)' : 'var(--secondary)',
                                                backgroundColor: isSelected ? 'rgba(190, 242, 100, 0.1)' : 'var(--background)',
                                                color: isSelected ? 'var(--accent)' : 'var(--text-secondary)',
                                                fontWeight: isSelected ? '600' : '500',
                                                fontSize: '0.875rem',
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                transition: 'all 0.2s'
                                            }}
                                            onClick={() => setMonth(mStr)}
                                        >
                                            {label.charAt(0).toUpperCase() + label.slice(1)}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {error && <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderLeft: '4px solid var(--error)', color: 'var(--error)', padding: '1rem', marginBottom: '1.5rem', borderRadius: '0.5rem' }}>{error}</div>}

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', minHeight: '300px' }}>
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <>
                        {/* KPIs */}
                        <div className="responsive-grid" style={{ marginBottom: '2rem' }}>
                            <KpiCard 
                                title="Ingresos Totales" 
                                value={summary ? formatCurrency(summary.totalRevenue) : '$0.00'} 
                                icon="💰"
                            />
                            <KpiCard 
                                title="Cortes Realizados" 
                                value={summary?.totalCuts || 0} 
                                icon="✂️"
                            />
                            <KpiCard 
                                title="Ticket Promedio" 
                                value={summary ? formatCurrency(summary.averageTicket) : '$0.00'} 
                                icon="📈"
                                subtitle="Ingreso por corte"
                            />
                        </div>

                        {/* Barber Performance */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: 'var(--text-primary)' }}>
                                Rendimiento por Barbero {viewMode === 'diario' ? 'del día' : 'del mes'}
                            </h2>
                            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                                {viewMode === 'diario' 
                                    ? 'Haz clic en un barbero para ver el detalle de los clientes atendidos en esta fecha.'
                                    : 'En vista mensual, el detalle de clientes individuales no está disponible por razones de rendimiento.'
                                }
                            </p>
                            <BarberPerformanceTable performances={performances} date={date} allowDetail={viewMode === 'diario'} />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default Reports;
